export interface CountryOption {
  code: string;
  label: string;
  aliases?: readonly string[];
}

export const COUNTRY_OPTIONS: readonly CountryOption[] = [
  { code: 'MY', label: 'Malaysia' },
  { code: 'SG', label: 'Singapore' },
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'JP', label: 'Japan' },
  { code: 'US', label: 'United States', aliases: ['USA'] },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom', aliases: ['UK', 'Great Britain'] },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'IT', label: 'Italy' },
] as const;

export function getCountryCodeLookupEntries() {
  return COUNTRY_OPTIONS.flatMap((country) => [
    [country.code.toLowerCase(), country.code] as const,
    [country.label.toLowerCase(), country.code] as const,
    ...(country.aliases ?? []).map((alias) => [alias.toLowerCase(), country.code] as const),
  ]);
}

const countryCodeByLabel = new Map(getCountryCodeLookupEntries());

export const ALLOWED_COUNTRY_CODES = new Set(
  COUNTRY_OPTIONS.map((country) => country.code)
);

export function isAllowedCountryCode(value: unknown): value is string {
  return typeof value === 'string' && ALLOWED_COUNTRY_CODES.has(value.trim().toUpperCase());
}

export function normalizeCountryCode(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }

  const upperValue = trimmedValue.toUpperCase();
  if (/^[A-Z]{2}$/.test(upperValue) && ALLOWED_COUNTRY_CODES.has(upperValue)) {
    return upperValue;
  }

  return countryCodeByLabel.get(trimmedValue.toLowerCase()) ?? '';
}

export const US_STATE_OPTIONS = [
  { code: 'AL', label: 'Alabama' },
  { code: 'AK', label: 'Alaska' },
  { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' },
  { code: 'CA', label: 'California' },
  { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' },
  { code: 'DE', label: 'Delaware' },
  { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' },
  { code: 'HI', label: 'Hawaii' },
  { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' },
  { code: 'IN', label: 'Indiana' },
  { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' },
  { code: 'KY', label: 'Kentucky' },
  { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' },
  { code: 'MD', label: 'Maryland' },
  { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' },
  { code: 'MN', label: 'Minnesota' },
  { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' },
  { code: 'MT', label: 'Montana' },
  { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' },
  { code: 'NH', label: 'New Hampshire' },
  { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' },
  { code: 'NY', label: 'New York' },
  { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' },
  { code: 'OH', label: 'Ohio' },
  { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' },
  { code: 'PA', label: 'Pennsylvania' },
  { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' },
  { code: 'SD', label: 'South Dakota' },
  { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' },
  { code: 'UT', label: 'Utah' },
  { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' },
  { code: 'WA', label: 'Washington' },
  { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' },
  { code: 'WY', label: 'Wyoming' },
  { code: 'DC', label: 'District of Columbia' },
] as const;
